import React, { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Spinner,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBolt, faCheck } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import socialApiClient from "../../../cores/helpers/services/social-api-client";
import { useMeCore } from "../../../cores/me";
import { useGlobalCore } from "../../../cores/globals";

interface CreditPackage {
  id: string;
  credits: number;
  prices: Record<string, number>;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  usd: "$",
  brl: "R$",
  clp: "$",
  pen: "S/",
};

const CURRENCY_LABELS: Record<string, string> = {
  usd: "USD",
  brl: "BRL",
  clp: "CLP",
  pen: "PEN",
};

function formatPrice(amountCents: number, currency: string): string {
  const symbol = CURRENCY_SYMBOLS[currency] || "";
  const divisor = currency === "clp" ? 1 : 100;
  const value = amountCents / divisor;
  const formatted =
    currency === "clp"
      ? Math.round(value).toLocaleString("es-CL")
      : value.toFixed(2);
  return `${symbol}${formatted}`;
}

interface Props {
  isOpen: boolean;
  toggle: () => void;
  onPurchaseComplete?: () => void;
}

export const BuyCreditsModal = ({
  isOpen,
  toggle,
  onPurchaseComplete,
}: Props) => {
  const intl = useIntl();
  const { companyId } = useMeCore();
  const { isDarkMode } = useGlobalCore();
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [currency, setCurrency] = useState("usd");

  const loadPackages = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data } = await socialApiClient().get(
        "nanobanana/credits/packages",
        { params: { companyId } }
      );
      setPackages(data?.payload || []);
    } catch {
      toast.error(
        intl.formatMessage({ id: "app.buyCredits.errLoadPackages" })
      );
    } finally {
      setLoading(false);
    }
  }, [companyId, intl]);

  useEffect(() => {
    if (isOpen) loadPackages();
  }, [isOpen, loadPackages]);

  const handlePurchase = useCallback(
    async (pkg: CreditPackage) => {
      if (!companyId) return;
      setPurchasing(pkg.id);
      try {
        const { data } = await socialApiClient().post(
          "nanobanana/credits/purchase",
          { packageId: pkg.id, currency },
          { params: { companyId } }
        );
        const url = data?.payload?.url;
        if (url) {
          window.location.href = url;
        } else {
          toast.error(
            intl.formatMessage({ id: "app.buyCredits.errNoCheckoutUrl" })
          );
        }
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ||
          intl.formatMessage({ id: "app.buyCredits.errPurchaseFailed" });
        toast.error(msg);
      } finally {
        setPurchasing(null);
      }
    },
    [companyId, currency, intl]
  );

  const availableCurrencies = Object.keys(CURRENCY_LABELS);

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      className={isDarkMode ? "dark-modal" : ""}
    >
      <ModalHeader toggle={toggle}>
        <FontAwesomeIcon icon={faBolt} className="mr-2 text-warning" />
        {intl.formatMessage({ id: "app.buyCredits.title" })}
      </ModalHeader>
      <ModalBody>
        <div className="mb-3 d-flex align-items-center gap-2">
          <span className="font-weight-bold mr-2">
            {intl.formatMessage({ id: "app.buyCredits.currency" })}:
          </span>
          {availableCurrencies.map((cur) => (
            <Button
              key={cur}
              size="sm"
              color={currency === cur ? "primary" : "outline-secondary"}
              onClick={() => setCurrency(cur)}
              className="mr-1"
            >
              {CURRENCY_LABELS[cur]}
            </Button>
          ))}
        </div>

        {loading && (
          <div className="text-center py-4">
            <Spinner color="primary" />
          </div>
        )}

        {!loading && packages.length === 0 && (
          <p className="text-muted text-center py-3">
            {intl.formatMessage({ id: "app.buyCredits.noPackages" })}
          </p>
        )}

        {!loading && packages.length > 0 && (
          <div className="row">
            {packages.map((pkg) => {
              const price = pkg.prices[currency];
              const isPopular = pkg.id === "plus";
              return (
                <div key={pkg.id} className="col-md-4 mb-3">
                  <div
                    className={`card h-100 shadow-sm ${
                      isPopular ? "border-primary" : ""
                    }`}
                  >
                    {isPopular && (
                      <div className="card-header bg-primary text-white text-center py-1 small font-weight-bold">
                        {intl.formatMessage({
                          id: "app.buyCredits.popular",
                        })}
                      </div>
                    )}
                    <div className="card-body d-flex flex-column align-items-center text-center">
                      <h3 className="mb-1">
                        <FontAwesomeIcon
                          icon={faBolt}
                          className="text-warning mr-1"
                        />
                        {pkg.credits}
                      </h3>
                      <p className="text-muted small mb-2">
                        {intl.formatMessage({ id: "app.buyCredits.credits" })}
                      </p>
                      <h4 className="font-weight-bold mb-3">
                        {price
                          ? formatPrice(price, currency)
                          : "—"}
                      </h4>
                      {price && (
                        <p className="text-muted small mb-3">
                          {formatPrice(
                            Math.round(price / pkg.credits),
                            currency
                          )}{" "}
                          / {intl.formatMessage({ id: "app.buyCredits.perCredit" })}
                        </p>
                      )}
                      <Button
                        color="primary"
                        block
                        disabled={!price || purchasing !== null}
                        onClick={() => handlePurchase(pkg)}
                        className="mt-auto"
                      >
                        {purchasing === pkg.id ? (
                          <Spinner size="sm" />
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faCheck} className="mr-1" />
                            {intl.formatMessage({
                              id: "app.buyCredits.buy",
                            })}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};
